import { Comment } from './Comment';
import { EventBasic } from './Event';
import { Member } from './Member';
import { BaseView } from './BaseView';
import { registerSchema } from './transform';

export class Post {
  static readonly CHARACTER_LIMIT_FOR_LARGE_FONT = 50;
  static isShortPostText(text: string | null | undefined): boolean {
    const postText = text ?? '';

    return (
      !/https?:\/\//i.test(postText) &&
      postText.length <= Post.CHARACTER_LIMIT_FOR_LARGE_FONT &&
      postText.length > 0
    );
  }

  Id: number = 0;
  Text: string | null = '';
  DateCreated: Date = new Date();

  Member: Member = new Member();

  TargetMember: Member = new Member();

  LikesCount: number = 0;
  CommentsCount: number = 0;
  IsNotification: boolean = false;
  IsSticky: boolean = false;
  IsAuthor: boolean = false;
  /** Viewer is staff and not the author: may delete the post (website only in the UI). */
  CanModerate: boolean = false;
  IsLiked: boolean = false;
  PhotoId?: number | null = null;
  PhotoUrl?: string | null = null;
  PhotoIconUrl?: string | null = null;
  PostId?: number | null = null;
  PostType: string = '';
  RelatedPostType?: string | null = null;
  Event?: EventBasic | null = null;
  CanEdit: boolean = false;
  OverQuota: QuotaTypes = QuotaTypes.None;
  RecentComments?: Comment[] | null = null;

  // Backing fields for parsed notification data
  private _parsedNotificationData: { originalText?: string | null; others?: number } | null = null;
  private _notificationDataParsed = false;

  private get parsedNotificationData(): { originalText?: string | null; others?: number } | null {
    if (!this._notificationDataParsed) {
      this._notificationDataParsed = true;

      if (!this.IsNotification || !this.Text) {
        this._parsedNotificationData = null;
      } else {
        try {
          this._parsedNotificationData = JSON.parse(this.Text);
        } catch {
          this._parsedNotificationData = null;
        }
      }
    }

    return this._parsedNotificationData;
  }

  get IsPhoto(): boolean {
    return !!this.PhotoId && this.PhotoId > 0;
  }

  get IsSystem(): boolean {
    return (
      this.PostType !== 'UserPost' && this.PostType !== 'WallToWall' && this.PostType !== 'NewPhoto'
    );
  }

  get IsShortPost(): boolean {
    return (this.PostType === 'UserPost' || this.Id == 0) && Post.isShortPostText(this.Text);
  }

  get HasText(): boolean {
    if (this.IsNotification) {
      return (this.NotificationText?.length ?? 0) > 0;
    }
    return (this.Text?.length ?? 0) > 0;
  }

  get IsEdited(): boolean {
    if (this.IsSystem) {
      return false;
    }
    if (this.IsSticky) {
      return false;
    }
    if (this.IsNotification) {
      return this.NotificationText?.endsWith('(Edited)') ?? false;
    }
    return this.Text?.endsWith('(Edited)') ?? false;
  }

  get NotificationOthersCount(): number {
    if (!this.IsNotification || !this.Text) return 0;

    const data = this.parsedNotificationData;
    return data?.others || 0;
  }

  get NotificationText(): string {
    if (!this.IsNotification || !this.Text) return this.Text ?? '';

    const data = this.parsedNotificationData;
    return data?.originalText ?? '';
  }

  get TextNotEdited(): string {
    const searchString = '(Edited)';

    // Get the appropriate text source
    const sourceText = this.IsNotification ? this.NotificationText : (this.Text ?? '');

    // Remove (Edited) suffix if present
    const text = sourceText.endsWith(searchString)
      ? sourceText.slice(0, -searchString.length)
      : sourceText;

    return text;
  }


}

export class PostsView extends BaseView {
  PostItems: Post[] = [];
  IsPremium?: boolean | null;
}

export enum QuotaTypes {
  None = 0,
  FullSizePhoto = 1,
  PhotoStream = 2,
}

registerSchema(Post, {
  DateCreated: 'date',
  Member: Member,
  TargetMember: Member,
  Event: EventBasic,
  RecentComments: [Comment],
});

registerSchema(PostsView, {
  PostItems: [Post],
});
